import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase } from '../shared/lib/supabase'
import { useAuth } from './AuthContext'

const OrgContext = createContext({})

export function OrgProvider({ children }) {
  const { user, profile } = useAuth()
  const [currentOrg, setCurrentOrg] = useState(null)
  const [orgs, setOrgs] = useState([])
  const [userOrgRole, setUserOrgRole] = useState(null)
  const [loading, setLoading] = useState(true)

  const isGlobalAdmin = profile?.role === 'global_admin'
  const isOrgAdmin = userOrgRole === 'org_admin' || userOrgRole === 'global_admin'

  const fetchOrgs = useCallback(async () => {
    if (!user || !profile) {
      setLoading(false)
      return
    }

    // Client users don't need org context
    if (profile.role === 'client') {
      setLoading(false)
      return
    }

    try {
      if (profile.role === 'global_admin') {
        // Global admin sees all organizations
        const { data, error } = await supabase
          .from('organizations')
          .select('*')
          .order('name')

        if (!error && data) {
          setOrgs(data)
          setUserOrgRole('global_admin')

          // Restore preference from localStorage or use first org
          const savedOrgId = localStorage.getItem('ssa-active-org-id')
          const saved = data.find(o => o.id === savedOrgId)
          setCurrentOrg(saved || data[0] || null)
        }
      } else {
        // Org admins/members see their own org via org_members
        const { data: memberships, error } = await supabase
          .from('org_members')
          .select('org_id, role, organizations(*)')
          .eq('user_id', user.id)

        if (!error && memberships?.length) {
          const orgList = memberships.map(m => m.organizations).filter(Boolean)
          setOrgs(orgList)
          setUserOrgRole(memberships[0].role)
          setCurrentOrg(orgList[0] || null)
        } else if (profile.org_id) {
          // Fallback: legacy admin with org_id on profile
          const { data: org } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', profile.org_id)
            .single()

          if (org) {
            setOrgs([org])
            setCurrentOrg(org)
            setUserOrgRole('org_admin')
          }
        }
      }
    } catch (err) {
      console.error('Error fetching org context:', err)
    } finally {
      setLoading(false)
    }
  }, [user, profile])

  useEffect(() => {
    fetchOrgs()
  }, [fetchOrgs])

  const switchOrg = useCallback((orgId) => {
    const org = orgs.find(o => o.id === orgId)
    if (org) {
      setCurrentOrg(org)
      localStorage.setItem('ssa-active-org-id', orgId)
    }
  }, [orgs])

  const value = {
    currentOrg,
    userOrgRole,
    isGlobalAdmin,
    isOrgAdmin,
    orgs,
    switchOrg,
    loading,
    refreshOrgs: fetchOrgs
  }

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  )
}

export function useOrg() {
  return useContext(OrgContext)
}

export default OrgContext
